import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Typography,
} from "@mui/material";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Download, KeyRound, Plus, SlidersHorizontal } from "lucide-react";
import { useState, type JSX } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import type { ResellerLicence } from "@xop-any-time/shared";

import { getLicenceCatalog, keys, listLicences } from "../../../lib/crmApi.js";
import { usePermissions } from "../../../auth/usePermissions.js";
import { newLicencePath } from "./CreateLicenceDialog.js";
import {
  LicenceFeatureSummary,
  LicenceKindChip,
  LicenceRenewalNextLine,
  LicenceStatusChip,
  LicenceWorkflowChip,
  expirySentence,
  takeLicenceFile,
} from "./licence-ui.js";

/**
 * The customer's licences, on the customer detail page.
 *
 * COMPOSED INTO THE ROUTE, not into the page. `/customers/:id` renders
 * xop-edge-staff's own `CustomersDetailPage`, and `src/crm/README.md` asks that
 * ported files stay diffable against their source so re-syncing is a `diff -r`.
 * A reseller-only licence card is exactly the kind of invention that rule is
 * about, so `App.tsx` renders this beside the ported page rather than inside it
 * — one line of composition, zero edits to the port.
 *
 * A LIST, NOT A CARD, since 2026-09-10: a licence is one APPLIANCE, so a
 * customer with two servers holds two of them, with different addresses, terms
 * and port splits. The old single-card shape could not say that.
 *
 * The capacity here is what one BOX may run — deliberately not the same figure
 * as the agreement's purchased ports above, which a customer with two boxes
 * splits between them.
 *
 * EACH ROW SAYS WHAT COMES NEXT (2026-09-15, later): its type (Trial / Full),
 * when it expires in one sentence, and — from `licence.renewal`, crm-api's
 * call — whether a successor is already generated, waiting for XOP, or
 * drafted, whether it can be renewed now, or when the window opens. The
 * features it grants are listed in the two sections every feature list in
 * this portal uses, through the catalogue the builder reads.
 */
export function CustomerLicenceCard(): JSX.Element | null {
  const { id } = useParams<{ id: string }>();
  const customerId = Number(id);
  const queryClient = useQueryClient();
  const { can } = usePermissions();
  const navigate = useNavigate();
  const [downloading, setDownloading] = useState<number | null>(null);
  const [failure, setFailure] = useState<string | null>(null);

  const { data, isPending, error } = useQuery({
    queryKey: keys.licences({ customerId }),
    queryFn: () => listLicences({ customerId }),
    enabled: Number.isFinite(customerId),
  });
  // The catalogue places each feature in its section. Its own query so a
  // failure here leaves the list standing, with legacy names in place.
  const catalogQuery = useQuery({ queryKey: keys.licenceCatalog, queryFn: () => getLicenceCatalog() });
  const catalog = catalogQuery.data?.features ?? [];

  // A failure here must not take the customer page down with it — the licence
  // list is one section of a page whose other half loaded fine.
  if (error) return null;

  const licences = data?.licences ?? [];

  async function take(licence: ResellerLicence): Promise<void> {
    setDownloading(licence.id);
    setFailure(null);
    try {
      await takeLicenceFile(licence);
      await queryClient.invalidateQueries({ queryKey: keys.licences({ customerId }) });
    } catch (err) {
      setFailure((err as Error).message);
    } finally {
      setDownloading(null);
    }
  }

  return (
    <Card sx={{ mt: 3 }}>
      <CardContent sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <KeyRound size={16} />
          <Typography sx={{ fontSize: 15, fontWeight: 600 }}>Licensing</Typography>
          {can("licences:edit") && (
            <Button
              size="small"
              variant="contained"
              startIcon={<Plus size={15} />}
              // Straight to the builder — nothing exists until Generate.
              onClick={() =>
                navigate(newLicencePath({ id: customerId, name: licences[0]?.customerName ?? `Customer #${customerId}` }))
              }
              sx={{ marginLeft: "auto" }}
            >
              Add licence
            </Button>
          )}
        </Box>
        <Typography sx={{ fontSize: 12.5, color: "text.secondary" }}>
          What each appliance is licensed to run. The agreement records what was purchased; a
          customer with more than one box splits that capacity between them.
        </Typography>

        {failure && (
          <Alert severity="error" onClose={() => setFailure(null)}>
            {failure}
          </Alert>
        )}

        {isPending ? (
          <CircularProgress size={20} />
        ) : licences.length === 0 ? (
          <Typography sx={{ fontSize: 13.5, color: "text.secondary" }}>
            No licences yet.
          </Typography>
        ) : (
          licences.map((licence) => (
            <Box
              key={licence.id}
              data-testid="customer-licence"
              sx={{
                display: "flex",
                flexDirection: "column",
                gap: 1.25,
                p: 1.5,
                border: 1,
                borderColor: "divider",
                borderRadius: 1,
              }}
            >
              <Box sx={{ display: "flex", flexWrap: "wrap", alignItems: "center", gap: 2 }}>
                <Box sx={{ minWidth: 180 }}>
                  <Typography sx={{ fontSize: 13.5, fontWeight: 600 }}>
                    {licence.label || "Appliance"}
                  </Typography>
                  <Typography sx={{ fontSize: 11, fontFamily: "monospace", color: "text.secondary" }}>
                    {licence.hardwareAddress}
                  </Typography>
                </Box>

                <Box>
                  <Typography sx={{ fontSize: 11.5, color: "text.secondary" }}>
                    Licensed to this appliance
                  </Typography>
                  <Typography sx={{ fontSize: 13 }}>
                    {licence.ports.audioConferencing} audio / {licence.ports.webConferencing} web
                  </Typography>
                </Box>

                <Chip size="small" variant="outlined" label={`${licence.featureKeys.length} features`} />

                <Box sx={{ display: "flex", alignItems: "center", gap: 1, flexWrap: "wrap" }}>
                  <LicenceKindChip licence={licence} />
                  <LicenceStatusChip licence={licence} />
                  <Typography sx={{ fontSize: 12, color: "text.secondary" }} data-testid="customer-licence-expiry">
                    {expirySentence(licence)}
                  </Typography>
                  <LicenceWorkflowChip licence={licence} />
                </Box>

                <Box sx={{ marginLeft: "auto", display: "flex", gap: 1 }}>
                  <Button
                    size="small"
                    component={Link}
                    to={`/licences/${licence.id}`}
                    startIcon={<SlidersHorizontal size={15} />}
                  >
                    Manage
                  </Button>
                  {licence.currentIssue && (
                    <Button
                      size="small"
                      variant="outlined"
                      startIcon={<Download size={15} />}
                      disabled={downloading === licence.id}
                      onClick={() => void take(licence)}
                    >
                      {downloading === licence.id ? "Downloading…" : "Download"}
                    </Button>
                  )}
                </Box>
              </Box>

              <LicenceRenewalNextLine licence={licence} />
              <LicenceFeatureSummary licence={licence} catalog={catalog} />
            </Box>
          ))
        )}
      </CardContent>
    </Card>
  );
}

export default CustomerLicenceCard;
